import CTASection from "@/components/CTASection";
import Navigation from "@/components/Navigation";
import DiscoverMoreSection from "@/components/DiscoverMoreSection";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ContainerScroll } from "@/components/ui/container-scroll-animation";
import FooterWithNewsletter from "@/components/ui/footer-column";
import { HowItWorksSection } from "@/components/ui/how-it-works-section";
import { navigateToEnterpriseDemo } from "@/utils/navigation";
import { CheckCircle, Save, Zap } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

function PlaybooksPage() {
  const navigate = useNavigate();

  const why = [
    {
      title: "Repeatable systems = consistent performance",
      desc: "Turn winning flows into reusable Playbooks your team can run anytime.",
    },
    {
      title: "Fewer handoffs = faster launches",
      desc: "Research, planning, creation, and publishing live in one stack.",
    },
    {
      title: "Brand-lock at every step = zero drift",
      desc: "Brand Vault applies tone, visuals, and guardrails throughout.",
    },
  ];

  const _process = [
    { label: "Research", description: "Pull insights, competitors, and market angles." },
    { label: "Plan", description: "Pick audiences, offers, channels, and timelines." },
    { label: "Ideate", description: "Generate hooks, angles, and content outlines." },
    { label: "Create", description: "Draft copy and visuals together—brand-locked." },
    { label: "Edit", description: "Tweak headlines, swap images, refine CTAs in-place." },
    { label: "Publish", description: "Push directly to LinkedIn, Instagram, Facebook." },
  ];

  const examples = [
    { title: "Campaign launch", desc: "Insights, content strategy, creative concepts and Instagram posts.", image: "/lovable-uploads/campaign-launch.jpg", alt: "Campaign launch promotional image" },
    { title: "Seasonal sales", desc: "Market analysis, media planning, activation ideas and Meta ads.", image: "/lovable-uploads/13605bcd-6a96-4fbf-bb21-91b8867d4df1.jpg", alt: "Flash Sale promotional image with fashion items" },
    { title: "Monthly videos", desc: "Trend forecasting, content ideas, brand scripts and AI-generated videos.", image: "/lovable-uploads/monthly-videos.jpg", alt: "Monthly videos promotional image" },
    { title: "Brand building", desc: "Competitor analysis, brand positioning, product concepts and public relations.", image: "/lovable-uploads/brand-building.jpg", alt: "Brand building promotional image" },
  ];

  return (
    <div className="min-h-screen bg-background mt-24 md:mt-0">
      <Navigation />

      <div style={{
        '--brand': '150 70% 45%',
        '--brand-foreground': '155 75% 55%'
      } as React.CSSProperties}>
        <ContainerScroll
          titleComponent={
            <>
              <div className="mb-4">
                <Badge className="bg-primary/10 text-primary border-primary/20">
                  Playbooks
                </Badge>
              </div>
              <h1 className="text-4xl font-semibold text-black dark:text-white mb-4">
              Proven playbooks - Research, creation, on-brand campaigns in one stack
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Stack research, planning, creation, and publishing into one-click systems your team can run anytime.
              </p>
              <div className="flex justify-center mt-8">
                <Button
                  id="playbooks-book-demo-button"
                  className="px-8 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors h-12"
                  onClick={() => navigateToEnterpriseDemo(navigate)}
                >
                  Book a Playbooks Demo
                </Button>
              </div>
            </>
          }
        >
          <img
            src="/lovable-uploads/18cd406b-3dd2-4f45-80e1-83f5e5f59739.jpg"
            alt="Playbooks workflow interface showing Research, Plan, Ideate, and Create stages"
            className="w-full h-full object-cover object-center rounded-2xl"
            draggable={false}
            width={975}
            height={482}
          />
        </ContainerScroll>
      </div>

      {/* Section 1: Why Playbooks Matter */}
      <section className="py-16 mt-[135px]">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">Why Playbooks Matter</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {why.map((w, i) => (
              <Card key={i} className="h-full">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-600 border border-purple-200 grid place-items-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold mb-2">{w.title}</div>
                    <p className="text-sm text-muted-foreground">{w.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <HowItWorksSection
        title="How Playbooks Work"
        steps={[
          "Research market insights",
          "Plan campaign strategy",
          "Generate creative concepts",
          "Create brand-locked content",
          "Review and refine output",
          "Publish across channels"
        ]}
        image={{
          src: "/lovable-uploads/23b043b3-d971-48ea-893b-e53a977f4e5d.jpg",
          alt: "Two marketing professionals collaborating on playbook workflow with Research, Plan, Ideate, and Create stages"
        }}
        className="py-16"
      />

      {/* Section 3: Signature Superpowers */}
      <section className="py-16">
        <div className="container mx-auto px-6 grid md:grid-cols-2 gap-8">
          <Card className="p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary border border-primary/20 grid place-items-center flex-shrink-0">
                <Zap className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">Quick Stacks</h3>
                <p className="text-muted-foreground">Build multi-stage campaigns in seconds. Drag-drop Agents, hit Run, and a turbo stack chains research, copy, visuals, and scheduling—one tweak updates every asset automatically.</p>
              </div>
            </div>
          </Card>
          <Card className="p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary border border-primary/20 grid place-items-center flex-shrink-0">
                <Save className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">Saved for Later</h3>
                <p className="text-muted-foreground">Love the flow? Name it, save it, and rerun with a single click. Your custom stacks live in a shared library, ready to duplicate or hand off to teammates whenever the brief returns.</p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      <CTASection title="Got a friend hungry for smarter marketing plays?" subtitle="Send them a special invite and help them level up with bonus signup credits on gimmefy Playbooks." buttonText="Tell a friend" buttonLink="/tellafriend" />

      {/* Section 4: Example Playbooks */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">Example Playbooks</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {examples.map((ex, i) => (
              <Card key={i} className="overflow-hidden">
                <img
                  src={ex.image}
                  alt={ex.alt}
                  className="aspect-[16/10] w-full object-cover rounded-t-lg"
                  width={318}
                  height={199}
                />
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    <h3 className="font-semibold">{ex.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{ex.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-primary/10 via-primary/5 to-background text-center">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">Build once. Win often.</h2>
          <Button size="lg" onClick={() => navigateToEnterpriseDemo(navigate)}>
            Book a Playbooks demo
          </Button>
        </div>
      </section>
      <DiscoverMoreSection currentPage="playbooks" />

      <FooterWithNewsletter />
    </div>
  );
}

export default PlaybooksPage;